/**
 * Admin Firestore Costs dashboard.
 *
 * Per-worker, per-task, per-collection Firestore read/write counters
 * sourced from the live `worker_firestore_stats` collection.  Split
 * off from the LLM-cost page so each cost driver has its own home —
 * LLM dollars at /admin/costs/llm, Firestore reads here.
 */
import React, {useMemo, useState} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {LineChart} from '@mui/x-charts/LineChart';
import {
  pcolors,
  cardSx,
  pageSx,
  pageTitleSx,
  sectionLabelSx,
  tableHeadRowSx,
  tableHeadCellSx,
  tableBodyCellSx,
} from '../../theme/planapusTheme';
import {useGetWorkerFirestoreStats} from '../../api/graphql_admin_worker_firestore_stats';
import {usePageTitle} from '../../hooks/usePageTitle';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

type StatsRange = '1h' | '2h' | '3h' | '6h' | '24h' | '72h' | '7d';

function statsRangeHours(r: StatsRange): number {
  switch (r) {
    case '1h':
      return 1;
    case '2h':
      return 2;
    case '3h':
      return 3;
    case '6h':
      return 6;
    case '24h':
      return 24;
    case '72h':
      return 72;
    case '7d':
      return 168;
  }
}

interface CollectionAgg {
  reads: number;
  writes: number;
  deletes: number;
}

interface TaskAgg {
  reads: number;
  writes: number;
  deletes: number;
  jobCount: number;
  totalDurationMs: number;
  byCollection: Map<string, CollectionAgg>;
}

interface WorkerAgg {
  workerId: string;
  source: string; // 'api' | 'bullmq-worker'
  shortSha: string;
  branch: string;
  bootedAt: string;
  lastSeenAt: string;
  totals: {reads: number; writes: number; deletes: number; jobCount: number; totalDurationMs: number};
  byTask: Map<string, TaskAgg>;
  byCollection: Map<string, CollectionAgg>;
}

type StatRow = ReturnType<typeof useGetWorkerFirestoreStats>['data'] extends
  | {getWorkerFirestoreStats: infer R}
  | undefined
  ? R extends Array<infer X>
    ? X
    : never
  : never;

function bumpCollection(
  byColl: Map<string, CollectionAgg>,
  rows: ReadonlyArray<{collection: string; reads: number; writes: number; deletes: number}>,
): void {
  for (const row of rows) {
    let c = byColl.get(row.collection);
    if (!c) {
      c = {reads: 0, writes: 0, deletes: 0};
      byColl.set(row.collection, c);
    }
    c.reads += row.reads;
    c.writes += row.writes;
    c.deletes += row.deletes;
  }
}

function aggregate(rows: StatRow[]): WorkerAgg[] {
  const byWorker = new Map<string, WorkerAgg>();
  for (const r of rows) {
    let agg = byWorker.get(r.workerId);
    if (!agg) {
      agg = {
        workerId: r.workerId,
        source: r.worker.serviceKey ?? 'bullmq-worker',
        shortSha: r.worker.shortSha,
        branch: r.worker.branch,
        bootedAt: r.worker.bootedAt,
        lastSeenAt: r.lastSeenAt,
        totals: {reads: 0, writes: 0, deletes: 0, jobCount: 0, totalDurationMs: 0},
        byTask: new Map(),
        byCollection: new Map(),
      };
      byWorker.set(r.workerId, agg);
    }
    agg.totals.reads += r.totals.reads;
    agg.totals.writes += r.totals.writes;
    agg.totals.deletes += r.totals.deletes;
    agg.totals.jobCount += r.totals.jobCount;
    agg.totals.totalDurationMs += r.totals.totalDurationMs;
    if (r.lastSeenAt > agg.lastSeenAt) agg.lastSeenAt = r.lastSeenAt;
    bumpCollection(agg.byCollection, r.byCollection);
    for (const tc of r.taskCounts) {
      let task = agg.byTask.get(tc.taskType);
      if (!task) {
        task = {
          reads: 0,
          writes: 0,
          deletes: 0,
          jobCount: 0,
          totalDurationMs: 0,
          byCollection: new Map(),
        };
        agg.byTask.set(tc.taskType, task);
      }
      task.reads += tc.reads;
      task.writes += tc.writes;
      task.deletes += tc.deletes;
      task.jobCount += tc.jobCount;
      task.totalDurationMs += tc.totalDurationMs;
      bumpCollection(task.byCollection, tc.byCollection);
    }
  }
  return Array.from(byWorker.values()).sort(
    (a, b) => b.totals.reads - a.totals.reads,
  );
}

function staleness(lastSeenAt: string): 'live' | 'idle' | 'gone' {
  const ageMs = Date.now() - Date.parse(lastSeenAt);
  if (ageMs < 2 * 60 * 1000) return 'live';
  if (ageMs < 10 * 60 * 1000) return 'idle';
  return 'gone';
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function sourceMeta(source: string): {label: string; bg: string; fg: string} {
  if (source === 'api')
    return {label: 'API · client polling', bg: pcolors.purple ? '#F3E8FF' : pcolors.blue[50], fg: pcolors.purple?.[500] ?? pcolors.blue[700]};
  return {label: 'Worker · background jobs', bg: pcolors.green[50], fg: pcolors.green[700]};
}

function fmtRelativeTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

interface TimePoint {
  label: string; // "MM-DD HH:00"
  reads: number;
  writes: number;
  deletes: number;
  jobCount: number;
  readsPerJob: number;
  writesPerJob: number;
  deletesPerJob: number;
}

/**
 * Group the per-(worker, hour) rows by hour bucket, sum reads/writes/
 * deletes/jobs across all workers, and normalize per job. Hourly is the
 * native granularity of `worker_firestore_stats` (docId carries
 * `YYYY-MM-DDTHH`), so sub-hour ranges (1h/2h) yield only 1–2 points.
 */
function buildTimeSeries(rows: StatRow[]): TimePoint[] {
  const byHour = new Map<
    string,
    {reads: number; writes: number; deletes: number; jobCount: number}
  >();
  for (const r of rows) {
    let a = byHour.get(r.hourBucket);
    if (!a) {
      a = {reads: 0, writes: 0, deletes: 0, jobCount: 0};
      byHour.set(r.hourBucket, a);
    }
    a.reads += r.totals.reads;
    a.writes += r.totals.writes;
    a.deletes += r.totals.deletes;
    a.jobCount += r.totals.jobCount;
  }
  return Array.from(byHour.entries())
    .sort((x, y) => x[0].localeCompare(y[0])) // chronological
    .map(([hb, a]) => {
      const jobs = a.jobCount;
      const norm = (n: number) => (jobs > 0 ? n / jobs : 0);
      return {
        // hb = "2026-06-06T14" → "06-06 14:00"
        label: `${hb.slice(5, 10)} ${hb.slice(11)}:00`,
        reads: a.reads,
        writes: a.writes,
        deletes: a.deletes,
        jobCount: jobs,
        readsPerJob: norm(a.reads),
        writesPerJob: norm(a.writes),
        deletesPerJob: norm(a.deletes),
      };
    });
}

export default function AdminFirestoreCosts({client}: Props) {
  usePageTitle('Admin – Firestore Costs');
  const [range, setRange] = useState<StatsRange>('24h');
  const sinceHours = statsRangeHours(range);
  const {data, loading, error} = useGetWorkerFirestoreStats(client, sinceHours);
  const rows = (data?.getWorkerFirestoreStats ?? []) as StatRow[];
  const workers = useMemo(() => aggregate(rows), [rows]);
  const series = useMemo(() => buildTimeSeries(rows), [rows]);
  const [openWorkerId, setOpenWorkerId] = useState<string | null>(null);

  // Reads split by source (API vs worker) — the headline: client-poll-
  // driven API reads dwarf the worker's background-job reads.
  const sourceSplit = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of workers) m.set(w.source, (m.get(w.source) ?? 0) + w.totals.reads);
    const rowsOut = Array.from(m.entries())
      .map(([source, reads]) => ({source, reads}))
      .sort((a, b) => b.reads - a.reads);
    const total = rowsOut.reduce((a, b) => a + b.reads, 0);
    return {rows: rowsOut, total};
  }, [workers]);

  const xLabels = series.map(p => p.label);
  // Thin x-axis labels to ~12 so 7d (168 hourly points) stays readable;
  // data + tooltip keep every point.
  const labelStep = Math.max(1, Math.ceil(series.length / 12));
  const showMark = series.length <= 24;
  const charts: Array<{
    title: string;
    key: 'readsPerJob' | 'writesPerJob' | 'deletesPerJob';
    raw: 'reads' | 'writes' | 'deletes';
    color: string;
  }> = [
    {title: 'Reads / job', key: 'readsPerJob', raw: 'reads', color: '#1565c0'},
    {title: 'Writes / job', key: 'writesPerJob', raw: 'writes', color: '#2e7d32'},
    {title: 'Deletes / job', key: 'deletesPerJob', raw: 'deletes', color: '#c62828'},
  ];

  return (
    <Box sx={pageSx}>
      <Box sx={{mb: 3}}>
        <Typography sx={pageTitleSx}>Firestore Costs</Typography>
        <Typography sx={{fontSize: 13, color: pcolors.textSecondary, mt: 0.5}}>
          Per-BullMQ-task counters from the live{' '}
          <code>worker_firestore_stats</code> collection. Includes workers that
          are no longer active — their last-seen mark tells you when they
          stopped.
        </Typography>
      </Box>

      <Box sx={{display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap'}}>
        {(['1h', '2h', '3h', '6h', '24h', '72h', '7d'] as StatsRange[]).map(r => (
          <Chip
            key={r}
            label={`Last ${r}`}
            size="small"
            onClick={() => setRange(r)}
            sx={{
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              bgcolor: r === range ? pcolors.blue[50] : pcolors.borderLight,
              color: r === range ? pcolors.blue[700] : pcolors.textSecondary,
              border:
                r === range
                  ? `1px solid ${pcolors.blue[500]}`
                  : `1px solid transparent`,
            }}
          />
        ))}
      </Box>

      {sourceSplit.total > 0 && (
        <Paper sx={{...cardSx, p: 2, mb: 2}}>
          <Typography sx={sectionLabelSx}>Reads by source · last {range}</Typography>
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mb: 1.5}}>
            Where your Firestore reads come from. API reads are
            client-poll-driven GraphQL resolver fetches; worker reads are
            background BullMQ jobs.
          </Typography>
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            {sourceSplit.rows.map(s => {
              const meta = sourceMeta(s.source);
              const pct = Math.round((s.reads / sourceSplit.total) * 100);
              return (
                <Box
                  key={s.source}
                  sx={{flex: '1 1 220px', border: `1px solid ${pcolors.border}`, borderRadius: 1.5, p: 1.5}}
                >
                  <Chip
                    size="small"
                    label={meta.label}
                    sx={{height: 20, fontSize: 11, fontWeight: 700, bgcolor: meta.bg, color: meta.fg, mb: 1}}
                  />
                  <Typography sx={{fontSize: 24, fontWeight: 800, color: pcolors.textPrimary, lineHeight: 1.1}}>
                    {fmtNum(s.reads)}
                  </Typography>
                  <Typography sx={{fontSize: 12, color: pcolors.textSecondary}}>
                    {pct}% of reads
                  </Typography>
                  <Box sx={{mt: 1, height: 6, borderRadius: 999, bgcolor: pcolors.borderLight, overflow: 'hidden'}}>
                    <Box sx={{width: `${pct}%`, height: '100%', bgcolor: meta.fg}} />
                  </Box>
                </Box>
              );
            })}
          </Box>
          {!sourceSplit.rows.some(s => s.source === 'api') && (
            <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 1.5, fontStyle: 'italic'}}>
              No API-source rows yet — the api service flushes its first batch ~60s
              after the next deploy. Until then only worker reads appear (≈2% of the
              true total; the rest were previously untracked).
            </Typography>
          )}
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error.message}
        </Alert>
      )}

      {series.length > 0 && (
        <Paper sx={{...cardSx, p: 2, mb: 2}}>
          <Typography sx={sectionLabelSx}>
            Per-job rates over time · last {range}
            {series.length === 1 && ' (single hour — stats are bucketed hourly)'}
          </Typography>
          <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mb: 1}}>
            Summed across all workers per hour, divided by jobs in that hour.
            Reads/writes/deletes are charted separately because their
            magnitudes differ by orders of magnitude.
          </Typography>
          {charts.map(c => (
            <Box key={c.key}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: c.color,
                  mt: 1,
                }}
              >
                {c.title}
              </Typography>
              <LineChart
                height={160}
                margin={{left: 64, right: 16, top: 8, bottom: 30}}
                xAxis={[
                  {
                    scaleType: 'point',
                    data: xLabels,
                    tickLabelStyle: {fontSize: 10},
                    tickInterval: (_v: string, i: number) => i % labelStep === 0,
                  },
                ]}
                series={[
                  {
                    data: series.map(p => p[c.key]),
                    color: c.color,
                    showMark,
                    curve: 'monotoneX',
                    area: true,
                    valueFormatter: (v: number | null) =>
                      v == null ? '' : fmtNum(Math.round(v)),
                  },
                ]}
                grid={{horizontal: true}}
              />
            </Box>
          ))}
        </Paper>
      )}

      {loading && workers.length === 0 ? (
        <Box sx={{p: 4, display: 'flex', justifyContent: 'center'}}>
          <CircularProgress size={24} />
        </Box>
      ) : workers.length === 0 ? (
        <Paper sx={{...cardSx, p: 3}}>
          <Typography sx={{fontSize: 13, color: pcolors.textSecondary}}>
            No worker has flushed Firestore stats in the last {sinceHours}h.
            If the worker was just deployed, give it a minute and refresh —
            the first flush lands ~60s after boot.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{...cardSx, p: 0}}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={tableHeadCellSx}>Worker</TableCell>
                  <TableCell sx={tableHeadCellSx}>Source</TableCell>
                  <TableCell sx={tableHeadCellSx}>State</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Reads</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Writes</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Deletes</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Jobs</TableCell>
                  <TableCell sx={tableHeadCellSx}>Last seen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workers.map(w => {
                  const state = staleness(w.lastSeenAt);
                  const isOpen = openWorkerId === w.workerId;
                  return (
                    <React.Fragment key={w.workerId}>
                      <TableRow
                        hover
                        onClick={() => setOpenWorkerId(isOpen ? null : w.workerId)}
                        sx={{cursor: 'pointer'}}
                      >
                        <TableCell sx={tableBodyCellSx}>
                          <Box sx={{display: 'flex', flexDirection: 'column'}}>
                            <Typography
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: 11,
                                fontWeight: 700,
                                color: pcolors.textPrimary,
                              }}
                            >
                              {w.workerId}
                            </Typography>
                            <Typography sx={{fontSize: 10, color: pcolors.textMuted}}>
                              {w.shortSha}
                              {w.branch !== 'main' ? ` · ${w.branch}` : ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          {(() => {
                            const meta = sourceMeta(w.source);
                            return (
                              <Chip
                                size="small"
                                label={w.source === 'api' ? 'API' : 'WORKER'}
                                sx={{height: 18, fontSize: 9, fontWeight: 700, bgcolor: meta.bg, color: meta.fg}}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Chip
                            size="small"
                            label={state.toUpperCase()}
                            sx={{
                              height: 18,
                              fontSize: 9,
                              fontWeight: 700,
                              bgcolor:
                                state === 'live'
                                  ? pcolors.green[50]
                                  : state === 'idle'
                                    ? pcolors.borderLight
                                    : pcolors.red[50],
                              color:
                                state === 'live'
                                  ? pcolors.green[700]
                                  : state === 'idle'
                                    ? pcolors.textSecondary
                                    : pcolors.red[700],
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                          {fmtNum(w.totals.reads)}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                          {fmtNum(w.totals.writes)}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                          {fmtNum(w.totals.deletes)}
                        </TableCell>
                        <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                          {fmtNum(w.totals.jobCount)}
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Typography sx={{fontSize: 11, color: pcolors.textSecondary}}>
                            {fmtRelativeTime(w.lastSeenAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{p: 0, bgcolor: pcolors.borderLight}}>
                            <WorkerTaskBreakdown agg={w} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

function WorkerTaskBreakdown({agg}: {agg: WorkerAgg}): React.JSX.Element {
  const tasks = useMemo(() => {
    return Array.from(agg.byTask.entries())
      .map(([taskType, c]) => ({taskType, ...c}))
      .sort((a, b) => b.reads - a.reads);
  }, [agg]);
  const collections = useMemo(() => {
    return Array.from(agg.byCollection.entries())
      .map(([collection, c]) => ({collection, ...c}))
      .sort((a, b) => b.reads - a.reads);
  }, [agg]);
  const topReads = tasks[0]?.reads ?? 1;
  const [openTask, setOpenTask] = useState<string | null>(null);
  return (
    <Box sx={{p: 2}}>
      {collections.length > 0 && (
        <Box sx={{mb: 2}}>
          <Typography sx={{...sectionLabelSx, mb: 1}}>
            By Firestore collection ({collections.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={tableHeadCellSx}>Collection</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Reads</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Writes</TableCell>
                  <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Deletes</TableCell>
                  <TableCell sx={tableHeadCellSx}>Share of reads</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collections.map(c => {
                  const share = (c.reads / Math.max(agg.totals.reads, 1)) * 100;
                  return (
                    <TableRow key={c.collection}>
                      <TableCell sx={tableBodyCellSx}>
                        <Typography sx={{fontFamily: 'monospace', fontSize: 11}}>
                          {c.collection}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                        {fmtNum(c.reads)}
                      </TableCell>
                      <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                        {fmtNum(c.writes)}
                      </TableCell>
                      <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                        {fmtNum(c.deletes)}
                      </TableCell>
                      <TableCell sx={tableBodyCellSx}>
                        <Box
                          sx={{
                            height: 6,
                            bgcolor: pcolors.borderLight,
                            borderRadius: 999,
                            overflow: 'hidden',
                            width: '100%',
                            maxWidth: 120,
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${share}%`,
                              bgcolor: pcolors.green[700],
                            }}
                          />
                        </Box>
                        <Typography sx={{fontSize: 9, color: pcolors.textMuted, mt: 0.25}}>
                          {share.toFixed(1)}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Typography sx={{...sectionLabelSx, mb: 1}}>
        Per task type ({tasks.length}) — click a row to see its
        per-collection breakdown
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadRowSx}>
              <TableCell sx={tableHeadCellSx}>Task type</TableCell>
              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Reads</TableCell>
              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Writes</TableCell>
              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Deletes</TableCell>
              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Jobs</TableCell>
              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Reads/job</TableCell>
              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Avg ms/job</TableCell>
              <TableCell sx={tableHeadCellSx}>Share</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map(t => {
              const share = (t.reads / Math.max(agg.totals.reads, 1)) * 100;
              const readsPerJob = t.jobCount > 0 ? t.reads / t.jobCount : 0;
              const msPerJob = t.jobCount > 0 ? t.totalDurationMs / t.jobCount : 0;
              const isOpen = openTask === t.taskType;
              const taskCollections = Array.from(t.byCollection.entries())
                .map(([collection, c]) => ({collection, ...c}))
                .sort((a, b) => b.reads - a.reads);
              return (
                <React.Fragment key={t.taskType}>
                  <TableRow
                    hover
                    onClick={() => setOpenTask(isOpen ? null : t.taskType)}
                    sx={{cursor: 'pointer'}}
                  >
                    <TableCell sx={tableBodyCellSx}>
                      <Typography sx={{fontFamily: 'monospace', fontSize: 11}}>
                        {t.taskType}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                      {fmtNum(t.reads)}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                      {fmtNum(t.writes)}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                      {fmtNum(t.deletes)}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                      {fmtNum(t.jobCount)}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                      {readsPerJob.toFixed(1)}
                    </TableCell>
                    <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                      {msPerJob.toFixed(0)}
                    </TableCell>
                    <TableCell sx={tableBodyCellSx}>
                      <Box
                        sx={{
                          height: 6,
                          bgcolor: pcolors.borderLight,
                          borderRadius: 999,
                          overflow: 'hidden',
                          width: '100%',
                          maxWidth: 120,
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${(t.reads / topReads) * 100}%`,
                            bgcolor: pcolors.blue[500],
                          }}
                        />
                      </Box>
                      <Typography sx={{fontSize: 9, color: pcolors.textMuted, mt: 0.25}}>
                        {share.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {isOpen && taskCollections.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{p: 2, bgcolor: pcolors.borderLight}}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            color: pcolors.textSecondary,
                            mb: 0.75,
                          }}
                        >
                          Collections touched by {t.taskType}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={tableHeadCellSx}>Collection</TableCell>
                              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Reads</TableCell>
                              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Writes</TableCell>
                              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Deletes</TableCell>
                              <TableCell sx={{...tableHeadCellSx, textAlign: 'right'}}>Reads/job</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {taskCollections.map(c => (
                              <TableRow key={c.collection}>
                                <TableCell sx={tableBodyCellSx}>
                                  <Typography sx={{fontFamily: 'monospace', fontSize: 11}}>
                                    {c.collection}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                                  {fmtNum(c.reads)}
                                </TableCell>
                                <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                                  {fmtNum(c.writes)}
                                </TableCell>
                                <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                                  {fmtNum(c.deletes)}
                                </TableCell>
                                <TableCell sx={{...tableBodyCellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                                  {t.jobCount > 0 ? (c.reads / t.jobCount).toFixed(1) : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
